import { useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";

// Public tool — no auth required. Paste-a-transcript workflow:
// (Screen 1) input → (Screen 2) parsed review + field edits → (Screen 3) download.

const STEPS = ["input", "review", "download"];

const DOC_META = [
  {
    key: "checklist",
    label: "Checklist Scorecard",
    body: "36 fixed criteria across the 3 rings with PASS / WATCH / FAIL status and transcript-sourced evidence.",
    length: "3–4 pages",
  },
  {
    key: "full_depth",
    label: "Full-Depth Brief",
    body: "Complete narrative: founder's story, problem, solution, market, GTM, embedded checklist, risks, next steps. Every number traces back to the transcript.",
    length: "12–15 pages",
  },
  {
    key: "simple",
    label: "Simple Brief",
    body: "One-to-two page condensed brief: idea, why, niche, GTM, scores, top 5 risks, verdict, one question to answer before spending a dollar.",
    length: "1–2 pages",
  },
];

const SAMPLE_TRANSCRIPT = `[INITIAL IDEA] Business Name — one-line description.

Ring 1
[FOUNDER] …
[INTERROGATOR] …
[RING 1 SCORE: 78/100 · signal: high · advance_ready: true]

Ring 2
[FOUNDER] …
[RING 2 SCORE: 72/100 · signal: medium · advance_ready: true]

Ring 3
[FOUNDER] Verdict: CONDITIONAL. Biggest strength: … Biggest risk: … Recommended first move: … One question before you spend a dollar: …
[RING 3 SCORE: 80/100 · signal: high · advance_ready: true]`;


export default function Formatter() {
  const [step, setStep] = useState("input");
  const [transcript, setTranscript] = useState("");
  const [intakeBrief, setIntakeBrief] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [session, setSession] = useState(null);
  const [blockReason, setBlockReason] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState(null);

  async function parse() {
    setParsing(true);
    setError(null);
    try {
      const resp = await fetch(`${API}/formatter/parse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          transcript,
          intake_brief: intakeBrief || undefined,
          business_name: businessName || undefined,
        }),
      });
      const body = await resp.json();
      if (!resp.ok) {
        throw new Error(body.detail || `HTTP ${resp.status}`);
      }
      setSession(body.session);
      setBlockReason(body.block_reason);
      setStep("review");
    } catch (e) {
      setError(e.message);
    } finally {
      setParsing(false);
    }
  }

  async function download(docType) {
    setDownloading(docType);
    setError(null);
    try {
      const resp = await fetch(`${API}/formatter/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session, doc_type: docType }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        let detail = text;
        try { detail = JSON.parse(text).detail; } catch (_) {}
        throw new Error(detail || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      const ext = docType === "zip" ? "zip" : "pdf";
      const safeName = (session?.business_name || "gauntlet").replace(/\s+/g, "_");
      a.download = `${safeName}_${docType}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
      toast.success(`${docType === "zip" ? "Package zip" : DOC_META.find((d) => d.key === docType)?.label} downloaded`);
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setDownloading(null);
    }
  }

  function updateSessionField(path, value) {
    setSession((s) => {
      const clone = JSON.parse(JSON.stringify(s));
      let ref = clone;
      for (let i = 0; i < path.length - 1; i++) {
        ref = ref[path[i]];
      }
      ref[path[path.length - 1]] = value;
      return clone;
    });
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-[960px] mx-auto px-8 py-12">
        <div className="mono-caps text-red-600 mb-2">The Gauntlet · Formatter</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-3">Document Package Generator</h1>
        <p className="text-slate-600 mb-10 max-w-2xl">
          Paste a completed Ring 1–3 Gauntlet transcript. Get the Checklist Scorecard,
          Full-Depth Brief, and Simple Brief as publish-ready PDFs. No account required.
        </p>

        {/* Step tracker */}
        <div className="flex items-center gap-2 mb-10" data-testid="formatter-steps">
          {STEPS.map((s, i) => {
            const active = STEPS.indexOf(step) >= i;
            return (
              <div key={s} className="flex items-center gap-2">
                <span
                  className={`mono-caps text-[0.6rem] px-2.5 py-1 rounded-full border ${
                    active ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-400 border-slate-300"
                  }`}
                  data-testid={`step-pill-${s}`}
                >
                  {i + 1}. {s.replace("_", " ")}
                </span>
                {i < STEPS.length - 1 && <span className="text-slate-300">→</span>}
              </div>
            );
          })}
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-rose-300 bg-rose-50 text-rose-900 px-5 py-3" data-testid="formatter-error">
            {error}
          </div>
        )}

        {/* Screen 1 — input */}
        {step === "input" && (
          <div data-testid="screen-input">
            <label className="mono-caps text-slate-500 text-[0.65rem] block mb-2">Business name (optional)</label>
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Auto-detected if left blank"
              className="input-flat w-full mb-6"
              data-testid="formatter-business-name"
            />

            <label className="mono-caps text-slate-500 text-[0.65rem] block mb-2">
              Ring 1–3 transcript <span className="text-red-600">*</span>
            </label>
            <textarea
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              placeholder={SAMPLE_TRANSCRIPT}
              rows={18}
              className="input-flat w-full font-mono text-xs leading-relaxed mb-2"
              data-testid="formatter-transcript"
            />
            <div className="text-slate-400 text-xs mb-8">
              Must include one <code>[RING N SCORE: XX/100 · signal: X · advance_ready: true|false]</code> tag per ring, plus a Ring 3 verdict block with all 5 fields.
            </div>

            <label className="mono-caps text-slate-500 text-[0.65rem] block mb-2">
              Founder intake brief (optional — improves Full-Depth output)
            </label>
            <textarea
              value={intakeBrief}
              onChange={(e) => setIntakeBrief(e.target.value)}
              placeholder="Paste the founder's original intake brief. Any [GAP: …] tokens will be carried forward as unresolved items."
              rows={8}
              className="input-flat w-full font-mono text-xs leading-relaxed mb-8"
              data-testid="formatter-intake"
            />

            <button
              onClick={parse}
              disabled={parsing || !transcript.trim()}
              className="btn-primary"
              data-testid="formatter-parse-btn"
            >
              {parsing ? "Parsing…" : "Parse session →"}
            </button>
          </div>
        )}

        {/* Screen 2 — parsed review */}
        {step === "review" && session && (
          <div data-testid="screen-review">
            <h2 className="font-display text-2xl mb-5">Review parsed session</h2>
            <p className="text-slate-600 mb-8">
              Fix any misparsed fields below, then generate the package. The three documents will share these
              exact values — a consistency check runs before any PDF is rendered.
            </p>

            <div className="mb-8">
              <label className="mono-caps text-slate-500 text-[0.65rem] block mb-1">Business name</label>
              <input
                value={session.business_name || ""}
                onChange={(e) => updateSessionField(["business_name"], e.target.value)}
                className="input-flat w-full"
                data-testid="review-business-name"
              />
            </div>

            <div className="mb-8">
              <div className="mono-caps text-slate-500 text-[0.65rem] mb-2">Ring scores</div>
              <div className="grid grid-cols-3 gap-4">
                {[1, 2, 3].map((r) => {
                  const ring = session[`ring${r}`] || {};
                  const cleared = ring.advance_ready;
                  return (
                    <div
                      key={r}
                      className={`card-flat ${cleared ? "border-emerald-200" : "border-rose-300 bg-rose-50"}`}
                      data-testid={`review-ring-${r}`}
                    >
                      <div className="mono-caps text-slate-500 text-[0.6rem] mb-1">Ring {r}</div>
                      <div className="font-display text-3xl leading-none mb-1">{ring.score ?? "—"}<span className="text-slate-400 text-sm ml-1">/100</span></div>
                      <div className="mono-caps text-[0.6rem] text-slate-500">
                        {ring.signal || "—"} · advance_ready:{" "}
                        <span className={cleared ? "text-emerald-600" : "text-rose-600 font-semibold"}>
                          {cleared ? "true" : "false"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {blockReason && (
              <div className="mb-8 rounded-lg border border-rose-300 bg-rose-50 text-rose-900 px-5 py-4" data-testid="review-block-reason">
                <div className="mono-caps text-[0.6rem] text-rose-700 mb-1">Generation blocked</div>
                {blockReason}
              </div>
            )}

            <div className="mb-8">
              <div className="mono-caps text-slate-500 text-[0.65rem] mb-2">Verdict block</div>
              {["verdict", "strength", "risk", "first_move", "open_question"].map((k) => (
                <div key={k} className="mb-3">
                  <label className="mono-caps text-slate-400 text-[0.55rem] block mb-1">{k.replace("_", " ")}</label>
                  <textarea
                    rows={k === "verdict" ? 1 : 2}
                    value={session.verdict_block?.[k] || ""}
                    onChange={(e) => updateSessionField(["verdict_block", k], e.target.value)}
                    className="input-flat w-full text-sm"
                    data-testid={`review-verdict-${k}`}
                  />
                </div>
              ))}
            </div>

            {session.intake_gaps?.length > 0 && (
              <div className="mb-8">
                <div className="mono-caps text-slate-500 text-[0.65rem] mb-2">
                  Unresolved [GAP] items from intake ({session.intake_gaps.length})
                </div>
                <ul className="text-sm text-slate-700 space-y-1" data-testid="review-intake-gaps">
                  {session.intake_gaps.map((g, i) => <li key={i}>• {g}</li>)}
                </ul>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setStep("input")}
                className="btn-secondary"
                data-testid="review-back"
              >
                ← Edit input
              </button>
              <button
                onClick={() => setStep("download")}
                disabled={!!blockReason}
                className="btn-primary"
                data-testid="review-generate-btn"
              >
                Generate package →
              </button>
            </div>
          </div>
        )}

        {/* Screen 3 — download */}
        {step === "download" && session && (
          <div data-testid="screen-download">
            <h2 className="font-display text-2xl mb-2">Package ready</h2>
            <p className="text-slate-600 mb-8">Download each PDF or grab all three as a zip.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {DOC_META.map((d) => (
                <div key={d.key} className="card-flat flex flex-col" data-testid={`download-card-${d.key}`}>
                  <div className="mono-caps text-slate-500 text-[0.6rem] mb-1">{d.length}</div>
                  <div className="font-display text-lg mb-2">{d.label}</div>
                  <p className="text-slate-600 text-sm mb-5 flex-1">{d.body}</p>
                  <button
                    className="btn-primary text-sm"
                    onClick={() => download(d.key)}
                    disabled={downloading === d.key}
                    data-testid={`download-btn-${d.key}`}
                  >
                    {downloading === d.key ? "…" : "Download PDF"}
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep("review")} className="btn-secondary" data-testid="download-back">
                ← Back to review
              </button>
              <button
                onClick={() => download("zip")}
                disabled={downloading === "zip"}
                className="btn-primary"
                data-testid="download-zip-btn"
              >
                {downloading === "zip" ? "…" : "Download all as .zip"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
